import { useState } from "react";
import { Search, ChevronDown, DollarSign, AlertCircle, RefreshCcw, X, CreditCard } from "lucide-react";

// Dummy data for payments
const paymentsData = [
  {
    id: 1,
    date: "25/04/2024",
    client: "Sarah Thompson",
    item: "Dermal Filler",
    amount: 200,
    method: "Cash",
    status: "Outstanding",
    receiptId: "RCP-001",
  },
  {
    id: 2,
    date: "25/04/2024",
    client: "Laura Johnson",
    item: "Skin Boosters",
    amount: 100,
    method: "Card",
    status: "Partial",
    receiptId: "RCP-002",
    paidAmount: 50,
  },
  {
    id: 3,
    date: "25/04/2024",
    client: "Ahmed",
    item: "Consent forms",
    amount: 30,
    method: "Card",
    status: "Paid",
    receiptId: "RCP-003",
  },
  {
    id: 4,
    date: "24/04/2024",
    client: "Rachel Green",
    item: "Laser Treatment",
    amount: 350,
    method: "Card",
    status: "Paid",
    receiptId: "RCP-004",
  },
  {
    id: 5,
    date: "24/04/2024",
    client: "Emma Thompson",
    item: "Microneedling",
    amount: 150,
    method: "Bank Transfer",
    status: "Refunded",
    receiptId: "RCP-005",
  },
  {
    id: 6,
    date: "25/04/2024",
    client: "Emily Turner",
    item: "Invoice INV-001256",
    amount: 75,
    method: "Cash",
    status: "Paid",
    receiptId: "RCP-006",
  },
  {
    id: 7,
    date: "21/04/2024",
    client: "Ahmed",
    item: "Hydrafacial",
    amount: 160,
    method: "Bank Transfer",
    status: "Outstanding",
    receiptId: "RCP-007",
  },
  {
    id: 8,
    date: "20/04/2024",
    client: "Sarah Thompson",
    item: "Skin care products",
    amount: 65,
    method: "Card",
    status: "Refunded",
    receiptId: "RCP-008",
  },
  {
    id: 9,
    date: "19/04/2024",
    client: "Sophie Richardson",
    item: "Invoice INV-001254",
    amount: 225,
    method: "Card",
    status: "Paid",
    receiptId: "RCP-009",
  },
];

// Receipt Modal Component
const ReceiptModal = ({ isOpen, onClose, payment }: { isOpen: boolean; onClose: () => void; payment: typeof paymentsData[0] | null }) => {
  if (!isOpen || !payment) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Receipt Details</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="text-center mb-6 pb-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Aesthetics</h2>
            <p className="text-sm text-gray-600">Receipt #{payment.receiptId}</p>
            <p className="text-sm text-gray-600">{payment.date}</p>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Client Information</h4>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Name:</span> {payment.client}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">Payment Details</h4>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Item:</span>
                <span className="text-gray-900 font-medium">{payment.item}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount:</span>
                <span className="text-gray-900 font-medium">£{payment.amount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Payment Method:</span>
                <span className="text-gray-900 font-medium">{payment.method}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${
                  payment.status === "Paid" ? "text-green-600" :
                  payment.status === "Outstanding" ? "text-orange-600" :
                  payment.status === "Partial" ? "text-purple-600" :
                  "text-yellow-600"
                }`}>
                  {payment.status}
                </span>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">Total:</span>
              <span className="text-2xl font-bold text-purple-600">£{payment.amount}</span>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Thank you for your business!</p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 flex gap-3 border-t border-gray-200">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium">
            Close
          </button>
          <button className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium">
            Print Receipt
          </button>
        </div>
      </div>
    </div>
  );
};

// Take Payment Modal
const TakePaymentModal = ({ isOpen, onClose, payment, onConfirm }: { 
  isOpen: boolean; 
  onClose: () => void; 
  payment: typeof paymentsData[0] | null;
  onConfirm: (amount: number, method: string) => void;
}) => {
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");

  if (!isOpen || !payment) return null;

  const remainingAmount = payment.status === "Partial" 
    ? payment.amount - (payment.paidAmount || 0)
    : payment.amount;

  const handleSubmit = () => {
    const amount = parseFloat(paymentAmount);
    if (amount > 0 && amount <= remainingAmount) {
      onConfirm(amount, paymentMethod);
      setPaymentAmount("");
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Take Payment</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
            <input
              type="text"
              value={payment.client}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Item</label>
            <input
              type="text"
              value={payment.item}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount Due: £{remainingAmount}
            </label>
            <input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="Enter amount"
              max={remainingAmount}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-600"
            >
              <option value="Cash">Cash</option>
              <option value="Card">Card</option>
              <option value="Bank Transfer">Bank Transfer</option>
            </select>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 flex gap-3 rounded-b-lg">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium">
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );
};

// Refund Modal
const RefundModal = ({ isOpen, onClose, payment, onConfirm }: { 
  isOpen: boolean; 
  onClose: () => void; 
  payment: typeof paymentsData[0] | null;
  onConfirm: () => void;
}) => {
  if (!isOpen || !payment) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">Confirm Refund</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-700 mb-4">
            Are you sure you want to refund this payment?
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Client:</span>
              <span className="text-gray-900 font-medium">{payment.client}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Item:</span>
              <span className="text-gray-900 font-medium">{payment.item}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Amount:</span>
              <span className="text-gray-900 font-medium">£{payment.amount}</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 flex gap-3 rounded-b-lg">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium">
            Cancel
          </button>
          <button 
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
          >
            Confirm Refund
          </button>
        </div>
      </div>
    </div>
  );
};

const AestheticPayments = () => {
  const [payments, setPayments] = useState(paymentsData);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("This Month");
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState<typeof paymentsData[0] | null>(null);
  const [showTakePaymentModal, setShowTakePaymentModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [paymentToProcess, setPaymentToProcess] = useState<typeof paymentsData[0] | null>(null);

  const itemsPerPage = 9;
  const timeFilterOptions = ["Today", "This Week", "This Month", "Last Month", "Last 3 Months", "This Year", "All Time"];

  // Filter data based on status
  const filteredData = payments.filter(payment => {
    const matchesSearch = searchTerm === "" ||
      payment.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.item.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.receiptId.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = activeFilter === "All" || payment.status === activeFilter;

    return matchesSearch && matchesStatus;
  });

  // Calculate stats
  const revenueToday = payments
    .filter(p => p.status === "Paid" && p.date === "25/04/2024")
    .reduce((sum, p) => sum + p.amount, 0);

  const outstandingBalance = payments
    .filter(p => p.status === "Outstanding")
    .reduce((sum, p) => sum + p.amount, 0);

  const refundsThisMonth = payments
    .filter(p => p.status === "Refunded")
    .reduce((sum, p) => sum + p.amount, 0);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handleTakePayment = (payment: typeof paymentsData[0]) => {
    setPaymentToProcess(payment);
    setShowTakePaymentModal(true);
  };

  const handleConfirmPayment = (amount: number, method: string) => {
    if (!paymentToProcess) return;

    setPayments(prev => prev.map(p => {
      if (p.id === paymentToProcess.id) {
        const remainingAmount = p.status === "Partial" 
          ? p.amount - (p.paidAmount || 0)
          : p.amount;

        if (amount >= remainingAmount) {
          return { ...p, status: "Paid" as const, method, paidAmount: p.amount };
        } else {
          return { 
            ...p, 
            status: "Partial" as const, 
            method,
            paidAmount: (p.paidAmount || 0) + amount 
          };
        }
      }
      return p;
    }));

    setPaymentToProcess(null);
  };

  const handleRefund = (payment: typeof paymentsData[0]) => {
    setPaymentToProcess(payment);
    setShowRefundModal(true);
  };

  const handleConfirmRefund = () => {
    if (!paymentToProcess) return;

    setPayments(prev => prev.map(p => 
      p.id === paymentToProcess.id 
        ? { ...p, status: "Refunded" as const }
        : p
    ));

    setPaymentToProcess(null);
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "Outstanding":
        return "bg-orange-100 text-orange-700";
      case "Partial":
        return "bg-purple-100 text-purple-700";
      case "Paid":
        return "bg-green-100 text-green-700";
      case "Refunded":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case "Cash":
        return "💵";
      case "Card":
        return "💳";
      case "Bank Transfer":
        return "🏦";
      default:
        return "💰";
    }
  };

  return (
    <div className="">
      <div className="flex flex-col w-full h-full">
        {/* Header */}
        <div className="bg-white px-6 py-6 border-b border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
              <p className="text-sm text-gray-500">Manage payments and transactions</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 px-4 lg:px-0">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 relative overflow-hidden">
            <div className="absolute top-4 right-4 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-purple-600" size={24} />
            </div>
            <div className="text-sm text-gray-600 mb-1">Revenue Today</div>
            <div className="text-3xl font-bold text-gray-900">£{revenueToday}</div>
            <div className="mt-2 inline-flex px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
              📅 This Month
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 relative overflow-hidden">
            <div className="absolute top-4 right-4 w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="text-orange-600" size={24} />
            </div>
            <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
              <span>⚠️</span>
              <span>Outstanding Balance</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">£{outstandingBalance}</div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 relative overflow-hidden">
            <div className="absolute top-4 right-4 w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <RefreshCcw className="text-purple-600" size={24} />
            </div>
            <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
              <span>🔄</span>
              <span>Refunds This Month</span>
            </div>
            <div className="text-3xl font-bold text-gray-900">£{refundsThisMonth}</div>
          </div>
        </div>

        {/* Filters Toolbar */}
        <div className="flex flex-wrap items-center gap-3 py-4 px-4 lg:px-0">
          {/* This Month Dropdown Filter */}
          <div className="relative">
            <button 
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              <span>📅</span>
              <span>{selectedTimeFilter}</span>
              <ChevronDown size={16} />
            </button>
            
            {showTimeDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[180px]">
                {timeFilterOptions.map(option => (
                  <button
                    key={option}
                    onClick={() => {
                      setSelectedTimeFilter(option);
                      setShowTimeDropdown(false);
                      setCurrentPage(1);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-purple-50 transition-colors ${
                      selectedTimeFilter === option ? "bg-purple-50 text-purple-700 font-medium" : "text-gray-700"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {["All", "Paid", "Outstanding", "Refunded"].map((filter) => (
              <button
                key={filter}
                onClick={() => {
                  setActiveFilter(filter);
                  setCurrentPage(1);
                }}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                  activeFilter === filter
                    ? "bg-purple-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search client, invoice, reference..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-600"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pb-6 px-4 lg:px-0">
          <div className="rounded-xl lg:border border-gray-200 lg:shadow-sm bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#885ABB] border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Client</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Item</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Method</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Actions</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedData.length > 0 ? (
                    paginatedData.map((payment) => (
                      <tr key={payment.id} className="hover:bg-purple-50/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payment.client}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{payment.item}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">£{payment.amount}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {getPaymentMethodIcon(payment.method)}
                            {payment.method}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${getStatusStyles(payment.status)}`}>
                            {payment.status === "Outstanding" && "⚠️"}
                            {payment.status === "Partial" && "⚪"}
                            {payment.status === "Paid" && "✅"}
                            {payment.status === "Refunded" && "🔄"}
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button 
                            onClick={() => setSelectedPayment(payment)}
                            className="text-purple-600 hover:text-purple-700 transition-colors text-sm font-medium"
                          >
                            View Receipt
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {payment.status === "Paid" ? (
                            <button 
                              onClick={() => handleRefund(payment)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                            >
                              Refund
                            </button>
                          ) : payment.status !== "Refunded" ? (
                            <button 
                              onClick={() => handleTakePayment(payment)}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                            >
                              Take Payment
                            </button>
                          ) : (
                            <span className="text-gray-400 text-sm">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-lg font-medium text-gray-500">No payments found</p>
                        <p className="text-sm text-gray-400">Try adjusting your filters</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {filteredData.length > 0 && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between px-4 lg:px-6 py-4 mt-4 gap-4 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-sm text-gray-600 font-medium order-2 sm:order-1">
                Showing <span className="text-gray-900">{startIndex + 1}</span> to <span className="text-gray-900">{Math.min(startIndex + itemsPerPage, filteredData.length)}</span> of <span className="text-gray-900">{filteredData.length}</span> results
              </div>
              <div className="flex items-center gap-3 order-1 sm:order-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Prev
                </button>
                <div className="bg-purple-50 px-3 py-1.5 rounded-lg text-purple-700 text-sm font-bold min-w-[80px] text-center border border-purple-100">
                  {currentPage} / {totalPages}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ReceiptModal isOpen={selectedPayment !== null} onClose={() => setSelectedPayment(null)} payment={selectedPayment} />
      <TakePaymentModal 
        isOpen={showTakePaymentModal} 
        onClose={() => {
          setShowTakePaymentModal(false);
          setPaymentToProcess(null);
        }} 
        payment={paymentToProcess}
        onConfirm={handleConfirmPayment}
      />
      <RefundModal 
        isOpen={showRefundModal} 
        onClose={() => {
          setShowRefundModal(false);
          setPaymentToProcess(null);
        }} 
        payment={paymentToProcess}
        onConfirm={handleConfirmRefund}
      />
    </div>
  );
};

export default AestheticPayments;